import { ApolloProvider } from '@apollo/client/react';
import { Amplify } from 'aws-amplify';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import log from 'loglevel';
import { useEffect, useMemo, useState } from 'react';

import AlertBar from './components/AlertBar';
import amplifyConfig from './config/AmplifyConfig';
import { configGA, initializeGA, usePageViews } from './config/analyticsTracker';
import apolloClient from './config/ApolloClient';
import { awardsQuery } from './services/Queries';
import { AppName } from './config/Constants';
import Footer from './layout/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import Metadata from './components/Metadata';
import NavigationBar from './layout/NavigationBar';
import { redirectUponSignIn } from './routes/RedirectUponSignIn';
import Routes from './routes/Routes';
import SideBar from './layout/SideBar';
import { standardizeUser, UserContext } from './config/UserContext';

import type { StandardizedUser } from './types/types';

// eslint-disable-next-line
process.env.NODE_ENV === 'development'
    ? log.setLevel('trace')
    : log.setLevel('error');

Amplify.configure(amplifyConfig);

// Todo: the way we protect routes is fragile. It relies on
// Amplify posting certain messages to their event bus but those
// don't seem stable since they changed when we fiddled with the
// config that solved the problem of not being able to get the Auth
// credentials after Login.

const stylesheet = {
    container: 'tw:pb-12',
    loggedInRow: 'tw:md:flex-nowrap tw:pt-0 tw:-mx-4',
    sideBarColumn: 'tw:z-[1] tw:bg-[#f7f7f7] tw:md:border-r tw:md:border-[#ececec] tw:px-4 tw:md:min-h-[calc(100vh-50px)]',
    contentColumn: 'tw:py-4 tw:px-4 tw:overflow-x-hidden',
};

function isFullyLoggedIn(user: StandardizedUser | null) {
    return user?.attributes?.sub && user.attributes.sub.length > 0;
}

function App(/* props */) {
    const [alertMessage, setAlertMessage] = useState('');
    const [amplifyUser, setAmplifyUser] = useState<StandardizedUser | null>(null);
    const [loading, setLoading] = useState(true);

    initializeGA();
    usePageViews();

    useEffect(() => {
        (async () => {
            try {
                await getCurrentUser();
                const attrs = await fetchUserAttributes();
                const user = standardizeUser(attrs);
                setAmplifyUser(user);
                if (user.attributes?.sub) {
                    apolloClient.query({
                        query: awardsQuery,
                        variables: { UserName: user.attributes.sub, AcceptedOnly: false },
                    });
                }
                setAlertMessage('');
                configGA();
                redirectUponSignIn();
            } catch (err) {
                log.info(err);
            }
            setLoading(false);
        })();
    }, []);

    const memoizedUser = useMemo(() => (amplifyUser), [amplifyUser]);

    if (loading) {
        return <LoadingSpinner />;
    }

    const showSideBar = amplifyUser?.attributes?.canGrant || amplifyUser?.attributes?.orgAdmin
        || amplifyUser?.attributes?.admin;

    return (
        <>
            <Metadata title={AppName} />
            <ApolloProvider client={apolloClient}>
                <UserContext.Provider value={memoizedUser}>
                    <NavigationBar />
                    <AlertBar message={alertMessage} />
                    <main
                      className={`tw:w-full tw:px-4 ${stylesheet.container}`}
                      // Handles mobile safari issue for background image
                    >
                        {isFullyLoggedIn(amplifyUser) ? (
                            <div className={`tw:flex tw:flex-wrap ${stylesheet.loggedInRow}`}>
                                {showSideBar && (
                                    <div
                                      className={`tw:w-full tw:md:w-3/12 tw:lg:w-2/12 ${stylesheet.sideBarColumn}`}
                                    >
                                        <SideBar />
                                    </div>
                                )}
                                <div
                                  className={`${stylesheet.contentColumn} tw:w-full ${showSideBar ? 'tw:md:w-9/12 tw:lg:w-10/12' : ''}`}
                                >
                                    <Routes />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <Routes />
                            </div>
                        )}
                    </main>
                    <Footer />
                </UserContext.Provider>
            </ApolloProvider>
        </>
    );
}

export default App;
